<template>
  <transition
    name="unpin-slide"
    @after-leave="afterLeave"
  >
    <div v-if="!isLeaving" class="internal-notes-card" :class="{ 'comments-open': showComments }">
      <!-- First row: Icon + Label + Pin -->
      <div class="card-header">
        <div class="card-icon-label">
          <div class="card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
              <path d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H14C14.55 0 15.0208 0.195833 15.4125 0.5875C15.8042 0.979167 16 1.45 16 2V18C16 18.55 15.8042 19.0208 15.4125 19.4125C15.0208 19.8042 14.55 20 14 20H2ZM2 18H14V2H12V9L9.5 7.5L7 9V2H2V18Z" fill="#25B8C2"/>
            </svg>
          </div>
          <span class="card-label">Internal Notes:</span>
        </div>
        <div class="pin-icon" @click.stop="handlePinClick">
          <img v-if="!isCardPinned" src="@/assets/cardActions/pin.svg" alt="Pin" />
          <img v-else src="@/assets/cardActions/unpin.svg" alt="Unpin" />
        </div>
      </div>

      <!-- Second row: Note content -->
      <div class="card-content">
        <p v-if="currentNote" class="note-text" v-html="displayNoteText"></p>
        <p v-else class="note-text empty">There are no internal notes recorded for this patient yet.</p>
      </div>

      <!-- Third row: Author | Time (gray) -->
      <div v-if="currentNote" class="card-meta">
        <span class="meta-text">{{ currentNote.author }}</span>
        <span class="meta-divider">|</span>
        <span class="meta-text">{{ currentNote.time }}</span>
      </div>

      <!-- Hover footer: Navigation actions -->
      <div class="card-expand" :class="{ 'justify-end': !showFooter, 'justify-between': showFooter }">
        <!-- Move button (left) -->
        <FoldableButton v-if="showFooter" customized_class="drag-handle" text="Move">
          <template #prefix>
            <img src="@/assets/cardActions/move.svg" alt="Move" />
          </template>
        </FoldableButton>
        <!-- Action buttons (right) -->
        <div class="flex flex-row gap-1 justify-end">
          <!-- Edit -->
          <FoldableButton
            v-if="!showComments"
            text="Edit"
            :disabled="!currentNote"
            @click="editNote"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M14.1727 2.37431C14.3169 2.59265 14.2928 2.88936 14.1006 3.08157L7.20634 9.97586C7.13567 10.0465 7.04744 10.0971 6.95075 10.1224L4.07942 10.8724C3.98578 10.8968 3.88924 10.8963 3.79854 10.8732C3.70212 10.8487 3.61229 10.7986 3.53953 10.7259C3.39832 10.5846 3.34257 10.3792 3.39304 10.1859L4.14304 7.31463C4.16514 7.23002 4.20661 7.14646 4.26397 7.08091L11.1838 0.164752C11.2444 0.10418 11.3174 0.0588451 11.3968 0.0312234C11.4556 0.0107432 11.518 0 11.5816 0C11.7307 0 11.8738 0.0592631 11.9793 0.164752L14.1006 2.28607C14.1281 2.31353 14.1521 2.34312 14.1727 2.37431ZM12.9074 2.68382L11.5816 1.358L5.19294 7.74662L4.7242 9.54119L6.51877 9.07244L12.9074 2.68382Z" fill="#374957"/>
                <path d="M12.8126 11.1827C13.0176 9.42952 13.0829 7.66408 13.0083 5.90309C13.0047 5.81878 13.0364 5.73674 13.0961 5.67706L13.8336 4.93957C13.9243 4.84889 14.0792 4.90646 14.0877 5.03441C14.2264 7.12665 14.1738 9.22839 13.93 11.3134C13.7525 12.8303 12.5343 14.0191 11.0253 14.1877C8.42532 14.4783 5.73782 14.4783 3.13788 14.1877C1.62884 14.0191 0.410592 12.8303 0.233178 11.3134C-0.0777259 8.6552 -0.0777259 5.9698 0.233178 3.31157C0.410592 1.79467 1.62884 0.605917 3.13789 0.437259C5.1102 0.216824 7.1329 0.163611 9.12955 0.277621C9.25823 0.284969 9.31689 0.440681 9.22575 0.53182L8.48134 1.27624C8.42231 1.33526 8.34138 1.36695 8.25796 1.36414C6.58814 1.30782 4.90697 1.37154 3.26284 1.5553C2.26526 1.66679 1.46615 2.454 1.35056 3.44226C1.04981 6.01365 1.04981 8.61134 1.35056 11.1827C1.46615 12.171 2.26526 12.9582 3.26284 13.0697C5.77973 13.351 8.38341 13.351 10.9003 13.0697C11.8979 12.9582 12.697 12.171 12.8126 11.1827Z" fill="#374957"/>
              </svg>
            </template>
          </FoldableButton>
          <!-- Previous -->
          <FoldableButton
            v-if="!showComments"
            text="Previous"
            :disabled="currentIndex <= 0 || notes.length === 0"
            @click="previousNote"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="#1E4670" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </template>
          </FoldableButton>
          <!-- Next -->
          <FoldableButton
            v-if="!showComments"
            text="Next"
            :disabled="currentIndex >= notes.length - 1 || notes.length === 0"
            @click="nextNote"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="#1E4670" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </template>
          </FoldableButton>
          <!-- Comments (only show if there are comments) -->
          <div
            v-if="currentNote?.replyCount > 0"
            class="expand-action comments-expand"
            :class="{ 'comments-shown': showComments }"
            @click.stop="toggleComments"
          >
            <!-- Eye crossed icon when comments shown -->
            <svg v-if="showComments" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 5.9748C15.79 5.9748 19.17 8.1048 20.82 11.4748C20.23 12.6948 19.4 13.7448 18.41 14.5948L19.82 16.0048C21.21 14.7748 22.31 13.2348 23 11.4748C21.27 7.08481 17 3.9748 12 3.9748C10.73 3.9748 9.51 4.1748 8.36 4.5448L10.01 6.1948C10.66 6.0648 11.32 5.9748 12 5.9748ZM10.93 7.1148L13 9.1848C13.57 9.4348 14.03 9.8948 14.28 10.4648L16.35 12.5348C16.43 12.1948 16.49 11.8348 16.49 11.4648C16.5 8.9848 14.48 6.9748 12 6.9748C11.63 6.9748 11.28 7.0248 10.93 7.1148ZM2.01 3.8448L4.69 6.52481C3.06 7.8048 1.77 9.5048 1 11.4748C2.73 15.8648 7 18.9748 12 18.9748C13.52 18.9748 14.98 18.6848 16.32 18.1548L19.74 21.5748L21.15 20.1648L3.42 2.4248L2.01 3.8448ZM9.51 11.3448L12.12 13.9548C12.08 13.9648 12.04 13.9748 12 13.9748C10.62 13.9748 9.5 12.8548 9.5 11.4748C9.5 11.4248 9.51 11.3948 9.51 11.3448ZM6.11 7.9448L7.86 9.69481C7.63 10.2448 7.5 10.8448 7.5 11.4748C7.5 13.9548 9.52 15.9748 12 15.9748C12.63 15.9748 13.23 15.8448 13.77 15.6148L14.75 16.5948C13.87 16.8348 12.95 16.9748 12 16.9748C8.21 16.9748 4.83 14.8448 3.18 11.4748C3.88 10.0448 4.9 8.8648 6.11 7.9448Z" fill="#374957"/>
            </svg>
            <!-- Eye icon when comments hidden -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.79 17.5 12 17.5C8.21 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z" fill="#374957"/>
            </svg>
            <span v-if="!showComments" class="comment-badge">{{ currentNote.replyCount }}</span>
            <span class="comment-text">{{ showComments ? 'Hide comments' : 'Comments' }}</span>
          </div>
          <!-- Reply -->
          <div
            v-if="showComments"
            class="expand-action reply-expand"
            :class="{ active: showReplyInput }"
            @click.stop="toggleReplyInput"
          >
            <!-- Active gray icon -->
            <svg v-if="showReplyInput" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 20" fill="none">
              <path d="M8 4V0L3 5L8 10V6C11.31 6 14 8.69 14 12C14 15.31 11.31 18 8 18C4.69 18 2 15.31 2 12H0C0 16.42 3.58 20 8 20C12.42 20 16 16.42 16 12C16 7.58 12.42 4 8 4Z" fill="#9E9E9E"/>
            </svg>
            <!-- Default icon -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 18 15" fill="none">
              <path d="M7 4V0L0 7L7 14V9.9C12 9.9 15.5 11.5 18 15C17 10 14 5 7 4Z" fill="#374957"/>
            </svg>
            <span class="reply-text" :class="{ 'gray': showReplyInput }">Reply</span>
          </div>
        </div>
      </div>

      <!-- Comments Section (expand on hover) -->
      <transition name="comments-expand">
        <div v-if="showComments && currentNote" class="comments-section" @click.stop>
          <div v-if="comments.length > 0" class="comments-list">
            <div
              v-for="comment in comments"
              :key="comment.id"
              class="comment-item"
            >
              <div class="card-meta">
                <span class="meta-text">{{ comment.author }}</span>
                <span class="meta-divider">|</span>
                <span class="meta-text">{{ comment.time }}</span>
              </div>
              <p class="comment-text" v-html="comment.displayHtml"></p>
            </div>
          </div>
          <div v-else class="no-comments">
            <p class="no-comments-text">No comments yet</p>
          </div>

          <!-- Reply Input -->
          <transition name="reply-expand">
            <div v-if="showReplyInput" class="reply-input-wrapper" @click.stop>
              <MentionTextarea
                v-model="replyText"
                :customerNameMap="customerNameMap"
                :customerList="customerList"
                placeholder="Add a comment ..."
              />
              <div class="reply-footer-row">
                <button class="send-btn" @click.stop="sendReply">Send</button>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useCardActions } from '@/composables/useCardActions';
import { getInternalNotesApi, getJwtTokenCustomerList, getRepliesApi, createReplyApi } from '@/api/internalNotes';
import { useMyPatientsStore } from '@/store/helpers';
import { notify } from '@/utils/notify';
import MentionTextarea from '@/views/MyPatient-demo/internalNoteComponents/MentionTextarea.vue';

// Module-level cache for customer name map
let cachedCustomerNameMap = null;
let cachedCustomerList = null;
let cachedToken = null;

// Define props
const props = defineProps({
  patient: {
    type: String,
    default: ''
  },
  cardData: {
    type: Object,
    default: () => ({})
  },
  showFooter: {
    type: Boolean,
    default: true
  }
});

// Define emits
const emit = defineEmits(['unpin-card', 'pin-card', 'edit-note', 'note-change']);

// Use card actions composable
const { isCardPinned, handlePinCard, handleUnpinCard } = useCardActions(props, emit);

// Get store for syncing current note ID across components
const { patientInfoStore } = useMyPatientsStore();

// State
const isLeaving = ref(false);
const notes = ref([]);
const currentIndex = ref(0);
const customerNameMap = ref({});
const customerList = ref([]);
const isLoading = ref(false);
const showComments = ref(false);
const comments = ref([]);
const showReplyInput = ref(false);
const replyText = ref('');

// Computed
const currentNote = computed(() => {
  if (notes.value.length === 0) return null;
  return notes.value[currentIndex.value] || null;
});

const displayNoteText = computed(() => {
  if (!currentNote.value?.text) return '';
  return transformMentionsToDisplay(currentNote.value.text);
});

// Methods
const handlePinClick = () => {
  if (isCardPinned.value) {
    if (props.showFooter) {
      isLeaving.value = true;
    } else {
      handleUnpinCard();
    }
  } else {
    handlePinCard();
  }
};

const afterLeave = () => {
  emit('unpin-card', props.cardData);
};

// Navigation
const previousNote = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    const noteId = notes.value[currentIndex.value]?.id;
    emit('note-change', noteId);
    // Sync to store for other instances
    patientInfoStore.actions.setCurrentInternalNoteId(noteId);
  }
};

const nextNote = () => {
  if (currentIndex.value < notes.value.length - 1) {
    currentIndex.value++;
    const noteId = notes.value[currentIndex.value]?.id;
    emit('note-change', noteId);
    // Sync to store for other instances
    patientInfoStore.actions.setCurrentInternalNoteId(noteId);
  }
};

// Get current note ID from store for syncing across instances
const currentNoteIdFromStore = computed(() =>
  patientInfoStore.getters.getCurrentInternalNoteId.value
);

// Toggle comments
const toggleComments = async () => {
  if (!currentNote.value) return;

  showComments.value = !showComments.value;

  if (showComments.value) {
    await fetchReplies();
  }
};

// Fetch replies for current note
async function fetchReplies() {
  if (!currentNote.value) return;

  try {
    const response = await getRepliesApi(currentNote.value.id, { include_nested: false });

    if (response && response.data) {
      comments.value = response.data.map(reply => mapReplyToFrontendFormat(reply));
    } else {
      comments.value = [];
    }
  } catch (error) {
    console.error('Error fetching replies:', error);
    comments.value = [];
  }
}

// Toggle reply input
const toggleReplyInput = () => {
  showReplyInput.value = !showReplyInput.value;
  if (!showReplyInput.value) {
    replyText.value = '';
  } else {
    // Scroll to bottom when reply input opens
    setTimeout(() => {
      const commentsSection = document.querySelector('.comments-section');
      if (commentsSection) {
        commentsSection.scrollTo({
          top: commentsSection.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 300);
  }
};

// Send reply
async function sendReply() {
  if (!replyText.value.trim() || !currentNote.value) return;

  try {
    await createReplyApi(currentNote.value.id, { content: replyText.value });

    notify({
      title: 'Success',
      message: 'Reply Sent Successfully',
      type: 'success',
      duration: 3000
    });

    replyText.value = '';
    showReplyInput.value = false;

    // Refresh replies
    await fetchReplies();

    // Emit update to refresh parent (update badge count)
    emit('note-change', currentNote.value.id);

  } catch (error) {
    console.error('Failed to send reply:', error);
    notify({
      title: 'Error',
      message: 'Failed to Send Reply',
      type: 'error',
      duration: 3000
    });
  }
}

// Map backend reply to frontend comment format
function mapReplyToFrontendFormat(reply) {
  const formattedTime = formatReplyTimestamp(reply.created_at);
  const authorId = reply.author_customer_id || reply.author?.customer_id;
  const authorName = customerNameMap.value[authorId] || authorId;
  const displayHtml = transformMentionsToDisplay(reply.content);

  return {
    id: reply.id,
    author: authorName,
    time: formattedTime,
    text: reply.content,
    displayHtml: displayHtml
  };
}

// Format reply timestamp
function formatReplyTimestamp(isoString) {
  if (!isoString) return '';

  const date = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const replyDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffTime = today - replyDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const timeStr = `${hours}:${minutes}`;

  if (diffDays === 0) {
    return `Today ${timeStr}`;
  } else if (diffDays === 1) {
    return `Yesterday ${timeStr}`;
  } else {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    return `${month} ${day}, ${timeStr}`;
  }
}

// Edit note
const editNote = () => {
  if (currentNote.value) {
    emit('edit-note', currentNote.value.id);
    // Slower smooth scroll to top when edit opens
    setTimeout(() => {
      const cardElement = document.querySelector('.internal-notes-card');
      if (cardElement) {
        const targetY = cardElement.getBoundingClientRect().top + window.pageYOffset - (window.innerHeight / 2) + (cardElement.offsetHeight / 2);
        const startY = window.pageYOffset;
        const distance = targetY - startY;
        const duration = 800; // slower scroll
        let startTime = null;

        const animateScroll = (currentTime) => {
          if (!startTime) startTime = currentTime;
          const timeElapsed = currentTime - startTime;
          const progress = Math.min(timeElapsed / duration, 1);
          const easeInOutCubic = progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;
          window.scrollTo(0, startY + distance * easeInOutCubic);
          if (timeElapsed < duration) {
            requestAnimationFrame(animateScroll);
          }
        };
        requestAnimationFrame(animateScroll);
      }
    }, 100);
  }
};

// Load customer name map
async function loadCustomerNameMap() {
  const currentToken = sessionStorage.getItem('token');

  if (cachedCustomerNameMap && cachedCustomerList && cachedToken === currentToken) {
    customerNameMap.value = cachedCustomerNameMap;
    customerList.value = cachedCustomerList;
    return;
  }

  if (cachedToken !== currentToken) {
    cachedCustomerNameMap = null;
    cachedCustomerList = null;
    cachedToken = null;
  }

  try {
    const response = await getJwtTokenCustomerList();
    const customerNameMapResponse = response.reduce((acc, item) => {
      return { ...acc, ...item };
    }, {});

    const fallbackMappings = {
      '999997': 'Vibrant IT3333'
    };
    const finalCustomerNameMap = { ...customerNameMapResponse, ...fallbackMappings };

    const customerListResponse = response.flatMap(item => {
      return Object.entries(item).map(([customer_id, name]) => ({
        customer_id,
        name
      }));
    });

    cachedCustomerNameMap = finalCustomerNameMap;
    cachedCustomerList = customerListResponse;
    cachedToken = currentToken;

    customerNameMap.value = finalCustomerNameMap;
    customerList.value = customerListResponse;
  } catch (error) {
    console.error('Failed to load customer name map:', error);
  }
}

// Transform mentions to display format
function transformMentionsToDisplay(text) {
  if (!text) return '';

  const mentionRegex = /@doctor_(\w+)_\w+/g;
  const transformed = text.replace(mentionRegex, (_match, customerId) => {
    const providerName = customerNameMap.value[customerId];
    if (providerName) {
      return `<span class="mention">@${providerName}</span>`;
    }
    return `<span class="mention">@Provider</span>`;
  });

  // Escape HTML
  let result = '';
  let lastIndex = 0;
  const spanRegex = /<span class="mention">([^<]+)<\/span>/g;
  let match;

  while ((match = spanRegex.exec(transformed)) !== null) {
    const beforeText = transformed.substring(lastIndex, match.index);
    result += escapeHtml(beforeText);
    result += match[0];
    lastIndex = spanRegex.lastIndex;
  }

  const remainingText = transformed.substring(lastIndex);
  result += escapeHtml(remainingText);

  return result;
}

function escapeHtml(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Format timestamp
function formatTimestamp(isoString) {
  if (!isoString) return '';

  const date = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const noteDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffTime = today - noteDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const timeStr = `${hours}:${minutes}`;

  if (diffDays === 0) {
    return `Today ${timeStr}`;
  } else if (diffDays === 1) {
    return `Yesterday ${timeStr}`;
  } else {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    return `${month} ${day}, ${timeStr}`;
  }
}

// Map notes to frontend format
function mapNotesToFrontendFormat(notesData) {
  return notesData.map(note => {
    const formattedTime = formatTimestamp(note.updated_at);
    const authorId = note.author_customer_id || note.author?.customer_id;
    const authorName = customerNameMap.value[authorId] || authorId;

    return {
      id: note.id,
      author: authorName,
      time: formattedTime,
      text: note.content,
      replyCount: note.reply_count || 0
    };
  });
}

// Fetch notes
async function fetchNotes() {
  if (!props.patient) return;

  isLoading.value = true;

  try {
    const response = await getInternalNotesApi(props.patient);

    if (response && response.data) {
      notes.value = mapNotesToFrontendFormat(response.data);

      // Try to restore the last selected note from store
      const storeNoteId = currentNoteIdFromStore.value;
      if (storeNoteId) {
        const index = notes.value.findIndex(n => n.id === storeNoteId);
        currentIndex.value = index !== -1 ? index : 0;
      } else {
        currentIndex.value = 0;
      }
    } else {
      notes.value = [];
      currentIndex.value = 0;
    }
  } catch (error) {
    console.error('Error fetching notes:', error);
    notes.value = [];
    currentIndex.value = 0;
  } finally {
    isLoading.value = false;
  }
}

// Lifecycle
onMounted(async () => {
  await loadCustomerNameMap();
  await fetchNotes();
});

// Watch for patient changes
watch(() => props.patient, async (newPatient, oldPatient) => {
  if (newPatient) {
    // Reset store state when switching patients
    if (newPatient !== oldPatient) {
      patientInfoStore.actions.setCurrentInternalNoteId(null);
    }
    await fetchNotes();
    // After fetching, check if store has a note ID to sync to
    const storeNoteId = currentNoteIdFromStore.value;
    if (storeNoteId && notes.value.length > 0) {
      const index = notes.value.findIndex(n => n.id === storeNoteId);
      if (index !== -1) {
        currentIndex.value = index;
      }
    }
  }
});

// Reset comments when note changes
watch(currentNote, (newNote, oldNote) => {
  if (newNote?.id !== oldNote?.id) {
    showComments.value = false;
    comments.value = [];
    showReplyInput.value = false;
    replyText.value = '';
  }
});

// Watch store changes to sync currentIndex across components
watch(currentNoteIdFromStore, (newNoteId) => {
  if (newNoteId && notes.value.length > 0) {
    const index = notes.value.findIndex(n => n.id === newNoteId);
    if (index !== -1 && index !== currentIndex.value) {
      currentIndex.value = index;
    }
  }
});
</script>

<style scoped>
/* Unpin animation */
.unpin-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.unpin-slide-leave-to {
  transform: translateX(-100%) scale(0.95);
  opacity: 0;
}

/* Card container */
.internal-notes-card {
  background-color: white;
  border-radius: 5px;
  padding: 12px 16px;
  box-shadow: 0px 1px 25px 0px rgba(74, 74, 74, 0.15);
  position: relative;
}

/* First row: Icon and Pin */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.card-icon-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-icon {
  background-color: #f2f2f2;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.card-label {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #666;
  margin: 0;
  line-height: 24px;
}

.pin-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.pin-icon:hover {
  background-color: #E5F1FB;
}

.pin-icon img {
  width: 18px;
  height: 18px;
}

/* Second row: Content */
.card-content {
  margin-left: 32px;
  margin-bottom: 8px;
}

.note-text {
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: var(--TrueBlack, #333);
  letter-spacing: 0.25px;
  margin: 0;
  max-height: 80px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.note-text.empty {
  padding: 8px 0;
  max-height: none;
  margin-left: -32px;
  text-align: center;
  color: #333;
  font-style: italic;
  font-family: Inter;
  font-size: 14px;
  letter-spacing: 0.25px;
  line-height: 20px;
}

.note-text::-webkit-scrollbar {
  width: 4px;
}

.note-text::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

/* Mentions in note text */
.note-text :deep(.mention) {
  color: #25B8C2;
  font-weight: 500;
}

/* Third row: Meta info (gray) */
.card-meta {
  display: flex;
  align-items: center;
  gap: 0;
  margin-left: 32px;
}

.meta-text {
  color: var(--SecondaryBlack, #666);
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: 0.1px;
}

.meta-divider {
  color: var(--SecondaryBlack, #999);
  margin: 0 6px;
  font-size: 10px;
}

/* Expanded card footer visibility */
.card-expand {
  display: flex;
  align-items: center;
  gap: 16px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  padding-top: 0;
  margin-top: 0;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  border-top-color: transparent;
  pointer-events: none;
  transition: max-height 300ms linear,
              opacity 300ms linear,
              padding-top 300ms linear,
              margin-top 300ms linear,
              border-top-color 300ms linear;
}

.internal-notes-card:hover .card-expand {
  /* ensure enough room for actions */
  max-height: 80px;
  opacity: 1;
  padding-top: 10px;
  margin-top: 10px;
  border-top-color: var(--DividerGray, #C4C4C4);
  pointer-events: auto;
}

/* Hide border when comments are open */
.internal-notes-card.comments-open .card-expand {
  border-top-color: transparent;
}

/* Base expand action style */
.expand-action {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1B79A0;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 5px;
  transition: background-color 0.2s;
}

.expand-action:hover {
  background-color: #E5F1FB;
}

.expand-action span {
  color: var(--color-Darkblue-000, #1E4670);
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

.expand-action img {
  width: 16px;
  height: 16px;
}

/* Expand buttons - icon only, expand on hover */
.edit-expand,
.comments-expand,
.reply-expand {
  display: flex;
  align-items: center;
  width: 26px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  transition: width 0.25s ease, padding 0.25s ease, gap 0.25s ease;
  gap: 0;
}

.edit-expand:hover,
.comments-expand:hover,
.reply-expand:hover {
  padding: 4px 10px;
  justify-content: flex-start;
  gap: 6px;
}

.edit-expand:hover { width: 74px; }
.comments-expand:hover { width: 115px; }
.comments-expand.comments-shown:hover { width: 145px; }
.reply-expand:hover { width: 72px; }

.edit-expand span,
.comments-expand span,
.reply-expand span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.3s ease;
  transition-delay: 0.05s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.edit-expand:hover span,
.comments-expand:hover span,
.reply-expand:hover span {
  opacity: 1;
}

.edit-expand svg,
.comments-expand svg,
.reply-expand svg {
  flex-shrink: 0;
}

/* Move expand button */
.move-expand {
  display: flex;
  align-items: center;
  width: 26px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  transition: width 0.25s ease, padding 0.25s ease, gap 0.25s ease;
  gap: 0;
}

.move-expand:hover {
  padding: 4px 10px;
  justify-content: flex-start;
  gap: 6px;
  width: 78px;
}

.move-expand span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.3s ease;
  transition-delay: 0.05s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.move-expand:hover span {
  opacity: 1;
}

.move-expand img {
  flex-shrink: 0;
}

/* Reply text styles */
.reply-expand .reply-text {
  white-space: nowrap;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.reply-expand .reply-text.gray {
  color: #1E4670;
}

.edit-expand.disabled,
.comments-expand.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.edit-expand.disabled:hover,
.comments-expand.disabled:hover {
  background-color: transparent;
  width: 26px;
  padding: 4px;
  gap: 0;
}

.edit-expand.disabled:hover span,
.comments-expand.disabled:hover span {
  opacity: 0;
}

/* Navigation buttons - expand on hover */
.prev-expand,
.next-expand {
  display: flex;
  align-items: center;
  width: 28px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  transition: width 0.25s ease, padding 0.25s ease, gap 0.25s ease;
  gap: 0;
  height: 28px;
  border-radius: 5px;
  cursor: pointer;
}

.prev-expand:hover:not(.disabled),
.next-expand:hover:not(.disabled) {
  padding: 4px 10px;
  justify-content: flex-start;
  gap: 6px;
  background-color: #E5F1FB;
}

.prev-expand:hover:not(.disabled) { width: 105px; }
.next-expand:hover:not(.disabled) { width: 78px; }

.prev-expand span,
.next-expand span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.3s ease;
  transition-delay: 0.05s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.prev-expand:hover:not(.disabled) span,
.next-expand:hover:not(.disabled) span {
  opacity: 1;
}

.prev-expand svg,
.next-expand svg {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.nav-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.prev-expand.disabled:hover,
.next-expand.disabled:hover {
  width: 28px;
  padding: 4px;
  gap: 0;
  background-color: transparent;
}

.prev-expand.disabled:hover span,
.next-expand.disabled:hover span {
  opacity: 0;
}

/* Comments button active state */
.expand-action.reply-expand.active {
  background-color: #E8EEF3;
}

/* Comment badge */
.comment-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 14px;
  height: 14px;
  padding: 1px 4px;
  border-radius: 7px;
  background: #1E4678;
  color: #FFF;
  font-family: Inter;
  font-size: 9px;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.15s ease;
}

.expand-action.comments-expand:hover .comment-badge {
  opacity: 0;
}

/* Comments expand animation - using transition for smoother effect */
.comments-expand-enter-active,
.comments-expand-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.comments-expand-enter-from,
.comments-expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
}

/* Comments Section */
.comments-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  padding-bottom: 12px;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  overflow-y: auto;
  max-height: 200px;
}

/* Hide border when comments are open */
.internal-notes-card.comments-open .comments-section {
  border-top-color: transparent;
  margin-top: 4px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.comments-section::-webkit-scrollbar {
  width: 4px;
}

.comments-section::-webkit-scrollbar-track {
  background: transparent;
}

.comments-section::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.comments-section::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.comment-item {
  padding: 8px 12px;
  background: #F8FBFD;
  border-radius: 4px;
}

.comment-item .card-meta {
  margin-left: 0;
}

.comment-text {
  color: #333;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
}

/* Mentions in comment text */
.comment-text :deep(.mention) {
  color: #25B8C2;
  font-weight: 500;
}

.no-comments {
  padding: 8px 0;
  text-align: center;
}

.no-comments-text {
  color: #999;
  font-family: Inter;
  font-size: 12px;
  font-style: italic;
  margin: 0;
}

/* Reply expand animation */
.reply-expand-enter-active {
  animation: reply-slide-down 0.25s ease-out;
}

.reply-expand-leave-active {
  animation: reply-slide-up 0.2s ease-in;
}

@keyframes reply-slide-down {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 250px;
    transform: translateY(0);
  }
}

@keyframes reply-slide-up {
  from {
    opacity: 1;
    max-height: 250px;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
}

/* Reply Input Wrapper */
.reply-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
  padding: 8px 12px;
  border: 1px solid #C8C8C9;
  border-radius: 4px;
  background: #FFF;
  position: relative;
}

.reply-input-wrapper :deep(.mention-editor) {
  font-family: Inter;
  font-size: 16px;
  line-height: 28px;
  color: #333;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  resize: none;
  min-height: 28px !important;
  max-height: 140px !important;
  overflow-y: auto;
}

.reply-character-counter {
  position: absolute;
  bottom: 6px;
  left: 8px;
  color: #999;
  font-family: Inter;
  font-size: 14px;
  pointer-events: none;
}

.reply-footer-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.send-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #1E4678;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
}
</style>
